<template>
    <div class="form-group row">
        <label class="col-xl-3 col-lg-3 col-form-label"> {{ translations.usesResources.resource }} </label>
        <div class="col-lg-9 col-xl-7">
            <div class="row">
                <div class="col-md-4">
                    <SelectFromDataSourceInput
                        ref="resourceSelect"
                        v-model="props.resource.resourceId"
                        :dataSource="resourceDataSource"
                        :formatter="formatResourceName"
                        :lazyInit="false"
                        :keyProp="'id'"
                        :showAllText="i18n('elements.select.pleaseSelect')"
                    />
                </div>

                <div class="col-md-8 row">
                    <label class="col-form-label ml-2">{{ translations.usesResources.number }}</label>
                    <div class="col-md-8" style="display: flex">
                        <NumberInput ref="numberOfUnitsInput" :model-value="props.resource.numberOfUnits" @update:modelValue="value => resource.numberOfUnits = value"/>
                    </div>
                    <div class="ml-4 mt-1">
                        <a @click="removeArticleResource(resource)" class="btn btn-sm btn-danger btn-icon btn-icon-md"
                            ><i class="la la-trash"></i
                        ></a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
    import type { IArticleResourceToCreateOrUpdate, IResource } from "@/data/model/Article";
    import { i18n } from "@/portal/Trans";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { ref } from "vue";

    import NumberInput from "@/portal/components/elements/NumberInput.vue";
    import SelectFromDataSourceInput from "@/portal/components/elements/SelectFromDataSourceInput.vue";
    import { Factory } from "@/data/dao/Factory";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";

    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);

    const props = defineProps<{
        resource: IArticleResourceToCreateOrUpdate;
    }>();

    const emit = defineEmits(["onRemoveArticleResource"]);

    const numberOfUnitsInput = ref<InstanceType<typeof NumberInput> | null>(null);
    const resourceSelect = ref<InstanceType<typeof SelectFromDataSourceInput> | null>(null);

    const resourceDataSource: IDataSource<IResource> = {
        getData: async () => {
            try {
                const resources = await catalogDao.getResources();
                return {
                    limit: 0,
                    offset: 0,
                    hasNext: false,
                    hasPrev: false,
                    items: resources,
                };
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    const formatResourceName = (resource: IResource) => {
        return translate(resource.name);
    };

    function removeArticleResource(resource: IArticleResourceToCreateOrUpdate) {
        emit("onRemoveArticleResource", resource);
    }

    function validate(): boolean {
        let hasErrors = false;

        numberOfUnitsInput.value!.setError(false);
        resourceSelect.value!.setError(false);

        if (props.resource.resourceId == null || props.resource.resourceId.length === 0) {
            hasErrors = true;
            resourceSelect.value!.setError(true);
        }
        if (props.resource.numberOfUnits == null || props.resource.numberOfUnits < 1) {
            numberOfUnitsInput.value!.setError(true);
            hasErrors = true;
        }

        return !hasErrors;
    }

    defineExpose({
        validate,
    });
</script>

<style scoped lang="scss"></style>
